import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Enrollment = () => {
  const [categories, setCategories] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    email: '',
    phone: '',
    birth_date: '',
    category_interest: '',
    enrollment_type: 'inscricao_clube',
    message: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/enrollments`, formData);
      toast.success('Inscrição enviada com sucesso!');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting enrollment:', error);
      toast.error('Erro ao enviar inscrição');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gray-50 py-16">
          <Card className="max-w-md w-full mx-4">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Inscrição Enviada!</h2>
              <p className="text-gray-600 mb-6">
                Recebemos sua inscrição e entraremos em contato em breve para dar prosseguimento.
              </p>
              <Button onClick={() => window.location.href = '/'} className="bg-red-600 hover:bg-red-700">
                Voltar ao Início
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4" data-testid="enrollment-title">Inscrição</h1>
          <p className="text-xl">Inscreva seu filho e faça parte da família Pequeninos do Jockey</p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="student_name">Nome do Aluno *</Label>
                    <Input 
                      id="student_name" 
                      name="student_name"
                      value={formData.student_name}
                      onChange={handleChange}
                      required
                      data-testid="student-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="birth_date">Data de Nascimento *</Label>
                    <Input 
                      id="birth_date" 
                      name="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={handleChange}
                      required
                      data-testid="birth-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="parent_name">Nome do Responsável *</Label>
                    <Input 
                      id="parent_name" 
                      name="parent_name"
                      value={formData.parent_name}
                      onChange={handleChange}
                      required
                      data-testid="parent-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input 
                      id="phone" 
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input 
                    id="email" 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="enrollment_type">Tipo de Inscrição *</Label>
                  <Select 
                    name="enrollment_type" 
                    value={formData.enrollment_type}
                    onValueChange={(value) => setFormData({ ...formData, enrollment_type: value })}
                    required
                  >
                    <SelectTrigger data-testid="enrollment-type-select">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aula_experimental">
                        🎯 Aula Experimental
                      </SelectItem>
                      <SelectItem value="inscricao_clube">
                        ⚽ Inscrição no Clube
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.enrollment_type === 'aula_experimental' 
                      ? 'Experimente uma aula antes de se matricular' 
                      : 'Matrícula completa no clube'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="category_interest">Categoria de Interesse *</Label>
                  <Select 
                    name="category_interest" 
                    value={formData.category_interest}
                    onValueChange={(value) => setFormData({ ...formData, category_interest: value })}
                    required
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name} ({cat.age_range})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Mensagem (Opcional)</Label>
                  <Textarea 
                    id="message" 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Conte-nos mais sobre o interesse do seu filho no futebol..."
                    data-testid="message-textarea"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
                  data-testid="submit-enrollment-button"
                >
                  Enviar Inscrição
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Enrollment;